import type { TileRole } from '../types';

/**
 * One board rule per trophy band. A later "Random venue" mode picks from this
 * list. Numbers here are the balance: spawn counts, score, and caps.
 *
 * Scoring stays on the duel points (10 per tile). The signature meter still
 * charges once per cleared tile of a critter's color — venue multipliers do
 * not charge the meter twice. Powerups and
 * signatures clear tiles through the same resolver, so they pick up a caterpillar
 * ring, and they do not get a honey clump or an ice cross (those are matches
 * only). Matching the Puddle Jump frog clears a plus on every hop.
 */
export const VENUE_IDS = [
  'mossy-meadow',
  'puddle-jump',
  'honey-hollow',
  'acorn-avalanche',
  'firefly-trails',
  'mushroom-bounce',
  'sneezy-dandelion',
  'picnic-panic',
  'rainbow-puddle',
  'caterpillar-crawl',
] as const;

export type VenueId = (typeof VENUE_IDS)[number];

export interface VenueDef {
  id: VenueId;
  name: string;
  /** One line, shown on the opening banner and the Rules popover. */
  rule: string;
  /** Best trophies. The highest band still at or under the player wins. */
  minTrophies: number;
  /** Tile role this venue paints, when it paints one. */
  role?: TileRole;
}

export const VENUES: readonly VenueDef[] = [
  {
    id: 'mossy-meadow',
    name: 'Snail Trail',
    rule: 'Match a snail and it zooms across two rows, clearing every tile, then goes out with a bang!',
    minTrophies: 0,
  },
  {
    id: 'puddle-jump',
    name: 'Puddle Jump',
    rule: 'Match the frog and it hops once per tile matched, splashing away a plus of tiles on every landing!',
    minTrophies: 200,
  },
  {
    id: 'honey-hollow',
    name: 'Honey Hollow',
    rule: 'Match a honey jar and it bursts, clearing every matching tile stuck to it!',
    minTrophies: 400,
    role: 'coated',
  },
  {
    id: 'acorn-avalanche',
    name: 'Cold Snap',
    rule: 'Match an ice cube and it shatters, blasting ice across its whole row and column!',
    minTrophies: 700,
    role: 'ice',
  },
  {
    id: 'firefly-trails',
    name: 'Firefly Spark',
    rule: 'Match a lantern and its spark leaps from tile to tile, one jump for every tile you matched!',
    minTrophies: 1000,
    role: 'lantern',
  },
  {
    id: 'mushroom-bounce',
    name: 'Mushroom Bounce',
    rule: 'Match a mushroom and it bounces twice, blasting a 3×3 of tiles on every landing!',
    minTrophies: 1300,
    role: 'mushroom',
  },
  {
    id: 'sneezy-dandelion',
    name: 'Sneezy Dandelion',
    rule: 'Matching a dandelion turns 3 tiles into your best scoring color.',
    minTrophies: 1600,
    role: 'dandelion',
  },
  {
    id: 'picnic-panic',
    name: 'Picnic Panic',
    rule: 'Match an ant and a line of ants marches out, snaking across the board and carrying off every tile it passes.',
    minTrophies: 2000,
  },
  {
    id: 'rainbow-puddle',
    name: 'Rainbow Puddle',
    rule: 'Match a puddle and a rainbow ripple washes the board, clearing every tile of that color.',
    minTrophies: 2500,
    role: 'puddle',
  },
  {
    id: 'caterpillar-crawl',
    name: 'Caterpillar Crawl',
    rule: 'Match a caterpillar and it spins silk around the outer ring. The cocoons burst into butterflies and those tiles clear.',
    minTrophies: 3000,
  },
];

/**
 * Balance knobs. Tests lock these so a later tune is a deliberate edit.
 *
 * - Snail: 3 to 5 pieces at the open, 1 step each per turn, never stacked.
 *   Matching one clears the tiles ahead of it, then one other row that has no
 *   snail, and removes it. If every row already has a snail, that second row
 *   is skipped. A dash, cascade, or power that lands on another snail removes
 *   that snail too, without starting a second dash. The bot prices the extra
 *   tiles, once per snail.
 * - Frog: 1 piece, 1 random hop per turn until it is matched. A match hops
 *   once per tile (5 when the match is longer). Each landing clears a plus.
 * - Honey: 10 honey jars, clumped in pairs. A jar matches as its own color.
 *   A match that includes one also clears the orthogonal same-color clump.
 * - Cold Snap (id stays acorn-avalanche): 3 ice cubes at the open, then a 9-in-20
 *   chance each turn, max 4, spaced at least 2 steps apart. A cube matches as its
 *   color. A match that includes one clears its row and its column. A cube only
 *   cleared by another cross does not fire. The bot prices those extra tiles.
 * - Firefly Spark: 4 lanterns at the open. If fewer than 2 remain when a turn
 *   starts, one more is painted. A lantern matches as its own color. A match
 *   that includes one sparks to the nearest same-color tile, then the next,
 *   once per matched tile (5+ caps at 5). Those tiles clear and score normally.
 * - Mushroom: 4 at the open, then one more when fewer than 2 remain.
 *   A matched mushroom clears a 3×3 around itself, then a second 3×3 at a
 *   seeded landing at least two tiles away. Each mushroom in the match does this.
 * - Dandelion: 2 tiles. 3 seeds become the matcher's hero color (the only color that
 *   also fills the signature meter). At most 2 sneezes per swap.
 * - Ant: 3 to 5 pieces at the open. Each one carries the tile it sits on, and
 *   a match of that color includes it. The march visits 6, 8, or 10 cells
 *   (match of 3, 4, or 5+), carrying those tiles off. One more ant arrives
 *   on a later turn if fewer than 3 remain. A matched ant still counts as a
 *   treat, worth 2 acorns when a ranked duel settles. The bot prices the
 *   extra tiles.
 * - Wild: exactly 1. It does not match by itself. The hint cycles the six colors.
 * - Puddle: 5 pieces, spread across icons. Each matches as the tile it sits on.
 *   A match that includes one clears every tile of that color. Restock toward 3.
 * - Caterpillar: 3 pieces, placed apart. A match spins silk around the outer ring
 *   of the 5×5 (Chebyshev distance 2) and clears those tiles. The inner 3×3 stays,
 *   except the matched tiles themselves. The bot prices the extra tiles once per caterpillar.
 */
export const VENUE_BALANCE = {
  snailCountMin: 3,
  snailCountMax: 5,
  snailSteps: 1,
  honeyCoats: 10,
  lanterns: 4,
  lanternFloor: 2,
  lanternAdd: 1,
  iceSeed: 3,
  iceChanceNum: 9,
  iceChanceDen: 20,
  iceMax: 4,
  mushrooms: 4,
  dandelions: 2,
  dandelionSeeds: 3,
  dandelionSneezes: 2,
  antCountMin: 3,
  antCountMax: 5,
  antAcorns: 2,
  puddleCount: 5,
  puddleFloor: 3,
  caterpillars: 3,
  caterpillarFloor: 2,
  caterpillarRestock: 1,
} as const;

const BY_ID = new Map<VenueId, VenueDef>(VENUES.map((venue) => [venue.id, venue]));

/** Cells an ant march visits, including the matched ant. */
export function antMarchLength(matchSize: number): number {
  if (matchSize >= 5) return 10;
  if (matchSize === 4) return 8;
  return 6;
}

export function venueById(id: string | null | undefined): VenueDef | null {
  if (!id) return null;
  return BY_ID.get(id as VenueId) ?? null;
}

/** Highest band whose trophy mark the player has reached. */
export function venueForTrophies(best: number): VenueDef {
  let found: VenueDef = VENUES[0];
  for (const venue of VENUES) {
    if (best >= venue.minTrophies) found = venue;
  }
  return found;
}

/** Stable pick for a later random-venue mode. The same seed always lands the same glen. */
export function randomVenueId(seed: number): VenueId {
  const index = Math.abs(seed >>> 0) % VENUE_IDS.length;
  return VENUE_IDS[index];
}

export function isVenueId(id: string | null | undefined): id is VenueId {
  return !!id && BY_ID.has(id as VenueId);
}
